import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import { getAppPartner } from '../Auth/authSelectors.js';
import { useLangContext } from '../../providers/LangProvider/LangProviderContext.js';
import { usePopupContext } from '../Popup/PopupContext.js';
import { getRequest, postRequest } from '../../app/request.js';
import amountFormat from '../../utils/amountFormat.js';
import { Loader } from '../../components/Loader/Loader.jsx';
import {
    MIN_PAYOUT_AMOUNT,
    PAYMENT_METHODS,
    PAYOUT_ACTIVE_STATUSES,
    PAYOUT_STATUS,
} from './paymentConstants.js';
import {
    PaymentActionsStyled,
    PaymentAmountRowStyled,
    PaymentBalanceStyled,
    PaymentDestinationSummaryStyled,
    PaymentHintStyled,
    PaymentHistoryTableStyled,
    PaymentNoticeStyled,
    PaymentSectionStyled,
    PaymentStatusStyled,
} from './styled/PaymentStyled.js';

function getStatusTone(status) {
    switch (status) {
        case PAYOUT_STATUS.PAID:
            return 'positive';
        case PAYOUT_STATUS.DECLINED:
            return 'negative';
        case PAYOUT_STATUS.APPROVED:
            return 'positive';
        case PAYOUT_STATUS.PENDING:
            return '';
        default:
            return 'muted';
    }
}

function Withdraw() {
    const { getLangPath } = useLangContext();
    const { catchPopup } = usePopupContext();
    const partnerInfo = useSelector(getAppPartner);

    const [amount, setAmount] = useState('');
    const [payouts, setPayouts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const balance = parseFloat(partnerInfo?.balance) || 0;
    const hasPaymentDetails = Boolean(partnerInfo?.payment_type && partnerInfo?.payment_info);

    const paymentMethodName = useMemo(() => {
        const method = _.find(PAYMENT_METHODS, { value: partnerInfo?.payment_type });
        return method?.name || partnerInfo?.payment_type || '';
    }, [partnerInfo?.payment_type]);

    const activePayout = useMemo(
        () => payouts.find((p) => PAYOUT_ACTIVE_STATUSES.includes(p.status)),
        [payouts],
    );

    const amountNum = parseFloat(amount);
    const amountValid = Number.isFinite(amountNum)
        && amountNum >= MIN_PAYOUT_AMOUNT
        && amountNum <= balance;

    const amountError = useMemo(() => {
        if (amount === '' || !Number.isFinite(amountNum)) {
            return null;
        }
        if (amountNum < MIN_PAYOUT_AMOUNT) {
            return getLangPath('payment.withdraw.min_hint', { min: MIN_PAYOUT_AMOUNT });
        }
        if (amountNum > balance) {
            return getLangPath('payment.withdraw.exceeds_balance');
        }
        return null;
    }, [amount, amountNum, balance, getLangPath]);

    const loadPayouts = useCallback(() => {
        setLoading(true);
        getRequest('/partner/payouts')
            .then((res) => {
                setPayouts(Array.isArray(res) ? res : []);
            })
            .catch((e) => {
                catchPopup(e.response);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [catchPopup]);

    useEffect(() => {
        loadPayouts();
    }, [loadPayouts]);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        if (!amountValid || activePayout || !hasPaymentDetails) {
            return;
        }
        setSubmitting(true);
        postRequest('/partner/payouts', { amount: amountNum })
            .then(() => {
                setAmount('');
                loadPayouts();
            })
            .catch((err) => {
                catchPopup(err.response);
            })
            .finally(() => {
                setSubmitting(false);
            });
    }, [amountValid, activePayout, hasPaymentDetails, amountNum, loadPayouts, catchPopup]);

    const handleWithdrawAll = useCallback(() => {
        if (balance >= MIN_PAYOUT_AMOUNT) {
            setAmount(String(Number(balance.toFixed(2))));
        }
    }, [balance]);

    const statusLabel = useCallback((status) => {
        const key = `payment.withdraw.status.${status}`;
        const translated = getLangPath(key);
        return translated === key ? status : translated;
    }, [getLangPath]);

    return <>
        <Loader show={loading || submitting} />

        <PaymentSectionStyled>
            <div className="section-header">
                <div className="section-heading">
                    <span className="section-step">{getLangPath('payment.step_withdraw')}</span>
                    <h4>{getLangPath('payment.withdraw.title')}</h4>
                    <p className="section-desc">{getLangPath('payment.withdraw.desc')}</p>
                </div>
                <PaymentBalanceStyled>
                    <span className="balance-label">{getLangPath('payment.withdraw.balance')}</span>
                    <span className="balance-value">${amountFormat(balance)}</span>
                </PaymentBalanceStyled>
            </div>

            {hasPaymentDetails && (
                <PaymentDestinationSummaryStyled>
                    <span className="dest-label">{getLangPath('payment.withdraw.to_label')}</span>
                    <span className="dest-method">{paymentMethodName}</span>
                    <span className="dest-info">{partnerInfo.payment_info}</span>
                </PaymentDestinationSummaryStyled>
            )}

            {activePayout ? (
                <PaymentNoticeStyled className="info">
                    {getLangPath('payment.withdraw.active_hint', {
                        amount: amountFormat(activePayout.amount),
                        status: statusLabel(activePayout.status),
                    })}
                </PaymentNoticeStyled>
            ) : (
                <Form onSubmit={handleSubmit}>
                    {!hasPaymentDetails && (
                        <PaymentNoticeStyled className="warning">
                            {getLangPath('payment.withdraw.need_payment_info')}
                        </PaymentNoticeStyled>
                    )}

                    <PaymentAmountRowStyled>
                        <Form.Group className="amount-group">
                            <Form.Label>{getLangPath('payment.withdraw.amount_label')}</Form.Label>
                            <div className="amount-input-row">
                                <Form.Control
                                    type="number"
                                    min={MIN_PAYOUT_AMOUNT}
                                    step="0.01"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    disabled={!hasPaymentDetails}
                                    isInvalid={Boolean(amountError)}
                                />
                                {hasPaymentDetails && balance >= MIN_PAYOUT_AMOUNT && (
                                    <Button
                                        variant="info"
                                        type="button"
                                        onClick={handleWithdrawAll}
                                    >
                                        {getLangPath('payment.withdraw.withdraw_all')}
                                    </Button>
                                )}
                            </div>
                            <PaymentHintStyled>
                                {getLangPath('payment.withdraw.min_hint', { min: MIN_PAYOUT_AMOUNT })}
                            </PaymentHintStyled>
                        </Form.Group>
                    </PaymentAmountRowStyled>

                    {amountError && (
                        <PaymentNoticeStyled className="error">
                            {amountError}
                        </PaymentNoticeStyled>
                    )}

                    <PaymentActionsStyled>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={!hasPaymentDetails || !amountValid || submitting}
                        >
                            {getLangPath('payment.withdraw.submit')}
                        </Button>
                    </PaymentActionsStyled>
                </Form>
            )}
        </PaymentSectionStyled>

        <PaymentSectionStyled>
            <div className="section-header">
                <div className="section-heading">
                    <h4>{getLangPath('payment.withdraw.history')}</h4>
                </div>
            </div>

            <PaymentHistoryTableStyled>
                <thead>
                    <tr>
                        <th className="col-id">{getLangPath('payment.withdraw.col_id')}</th>
                        <th className="col-amount">{getLangPath('payment.withdraw.col_amount')}</th>
                        <th>{getLangPath('payment.withdraw.col_status')}</th>
                        <th className="col-date">{getLangPath('payment.withdraw.col_date')}</th>
                    </tr>
                </thead>
                <tbody>
                    {payouts.map((item) => (
                        <tr key={item.id}>
                            <td className="col-id" data-label={getLangPath('payment.withdraw.col_id')}>
                                {item.id}
                            </td>
                            <td className="col-amount" data-label={getLangPath('payment.withdraw.col_amount')}>
                                ${amountFormat(item.amount)}
                            </td>
                            <td data-label={getLangPath('payment.withdraw.col_status')}>
                                <PaymentStatusStyled className={getStatusTone(item.status)}>
                                    <span className="indicator" />
                                    {statusLabel(item.status)}
                                </PaymentStatusStyled>
                            </td>
                            <td className="col-date" data-label={getLangPath('payment.withdraw.col_date')}>
                                {moment(item.created_at).format('YYYY-MM-DD HH:mm')}
                            </td>
                        </tr>
                    ))}
                    {!payouts.length && (
                        <tr>
                            <td className="empty-cell" colSpan={4}>
                                {getLangPath('payment.withdraw.empty')}
                            </td>
                        </tr>
                    )}
                </tbody>
            </PaymentHistoryTableStyled>
        </PaymentSectionStyled>
    </>;
}

export default Withdraw;
